import React, { useState, useEffect, FC, KeyboardEvent } from 'react'
import moment from 'moment'
import useOutsideClick from '../../Core/Hooks/useOutsideClick/useOutsideClick'

import Flex from '../../Layout/Flex/Flex'
import Icon from '../../Core/Iconography/Icon/Icon'
import Font from '../../Core/Typography/Font/Font'
import Day from '../../Atoms/Day/Day'
import Dropdown from '../../Atoms/Dropdown/Dropdown'
import {
  DatePickerWrapper,
  CalendarWrapper,
  DayCell,
  InputStyle,
} from './DatePicker.styles'
import {
  DatePickerProps,
  DayDataCellType,
  GetDayByIndexType,
  YearsDropdownDataType,
  DisabledRuleType,
} from './types'
import { ChangeArgs } from '../../Atoms/Dropdown/types'

const DISPLAY_DATE_FORMAT = 'DD-MM-YYYY'
const CURRENT_MONTH_FORMAT = 'MM-YYYY'

export const sliceIntoChunks = (
  list: unknown[],
  chunkSize: number
): DayDataCellType[][] => {
  const res = []
  for (let i = 0; i < list.length; i += chunkSize) {
    res.push(list.slice(i, i + chunkSize))
  }
  return res
}

export const getDataByCurrentMonth = (
  currentMonth: string,
  selectedDay: string,
  disabledRule: DisabledRuleType
): DayDataCellType[] => {
  const maxLength = 42 // 7 * 6
  const today: string = moment().format(DISPLAY_DATE_FORMAT)

  const daysInCurrentMonth: number = moment(
    currentMonth,
    CURRENT_MONTH_FORMAT
  ).daysInMonth()
  const startWeekIndex = Number(
    moment(`01-${currentMonth}`, DISPLAY_DATE_FORMAT).format('d')
  )

  const lastMonth: string = moment(currentMonth, CURRENT_MONTH_FORMAT)
    .subtract(1, 'months')
    .format(CURRENT_MONTH_FORMAT)
  const nextMonth: string = moment(currentMonth, CURRENT_MONTH_FORMAT)
    .add(1, 'months')
    .format(CURRENT_MONTH_FORMAT)

  const daysInLastMonth: number = moment(lastMonth, CURRENT_MONTH_FORMAT).daysInMonth()

  const addDayToMonth = (month: string, day: number): string => {
    return `${day <= 9 ? '0' + day : day}-${month}`
  }
  const getDayByIndex = (index: number): GetDayByIndexType => {
    // Last Month
    if (index < startWeekIndex) {
      const value: number = daysInLastMonth - (startWeekIndex - 1 - index)
      return {
        isCurrentMonth: false,
        date: addDayToMonth(lastMonth, value),
        value,
      }
    }

    // Next Month
    if (index > startWeekIndex + daysInCurrentMonth - 1) {
      const value: number = index + 1 - (startWeekIndex + daysInCurrentMonth)
      return {
        isCurrentMonth: false,
        date: addDayToMonth(nextMonth, value),
        value,
      }
    }

    // Current Month
    const value: number = index + 1 - startWeekIndex
    return {
      isCurrentMonth: true,
      date: addDayToMonth(currentMonth, value),
      value,
    }
  }

  return Array(maxLength)
    .fill(null)
    .map((_, index) => {
      const dayTuple: GetDayByIndexType = getDayByIndex(index)
      const isToday: boolean = today === dayTuple?.date

      return {
        isToday,
        index,
        disabled: disabledRule?.on
          ? disabledRule?.check(dayTuple?.date)
          : false,
        isCurrentMonth: dayTuple?.isCurrentMonth,
        tabIndex: dayTuple?.isCurrentMonth ? 0 : -1,
        'aria-selected':
          isToday || selectedDay === dayTuple?.date ? 'true' : 'false',
        'data-date': dayTuple?.date,
        'data-date-format': DISPLAY_DATE_FORMAT,
        value: dayTuple?.value,
      }
    })
}

export const updateCurrentMonth = (
  currentMonth: string,
  action?: string
): string => {
  if (action === 'lastMonth') {
    return moment(currentMonth, CURRENT_MONTH_FORMAT)
      .subtract(1, 'months')
      .format(CURRENT_MONTH_FORMAT)
  }

  if (action === 'nextMonth') {
    return moment(currentMonth, CURRENT_MONTH_FORMAT).add(1, 'months').format(CURRENT_MONTH_FORMAT)
  }

  return currentMonth
}

export const getWeekLabelsList = (list?: string[][]): string[][] => {
  if (Array.isArray(list)) return list

  return moment.weekdays().map(day => [day.slice(0, 3), day])

  /* If the tuple array is not provide in the correct it will be genereted with moment in English what will look like this:
  [
    ["Sun", "Sunday"],
    ["Mon", "Monday"],
    ["Tue", "Tuesday"],
    ["Wed", "Wednesday"],
    ["Thu", "Thursday"],
    ["Fri", "Friday"],
    ["Sat", "Saturday"]
  ]
  */
}

export const getMonthLabelsList = (list?: string[][]): { label: string }[] => {
  if (Array.isArray(list)) return list.map(tuple => ({ label: tuple?.[0] || '' }))

  return moment.monthsShort().map(label => ({ label }))
}

export const getIndexByCurrentMonth = (currentMonth: string) => {
  return Number(moment(currentMonth, CURRENT_MONTH_FORMAT).format('M')) - 1
}

export const getYearsInitialData = (
  currentMonth: string,
  noFuture?: boolean
): YearsDropdownDataType => {
  const length = 100
  const futureRange = noFuture ? 0 : 10
  const year = Number(moment(currentMonth, CURRENT_MONTH_FORMAT).format('YYYY'))
  const list = Array.from(
    { length },
    (_, index) => year + futureRange - index
  )

  return {
    selectedIndex: list.indexOf(year),
    optionsList: list.map(label => ({ label })),
  }
}

export const isItFuture = (day: string): boolean => {
  return (
    0 >= moment().diff(moment(day, DISPLAY_DATE_FORMAT).subtract(1, 'days'), 'days')
  )
}

export const isItPast = (day: string): boolean => {
  return (
    0 <= moment().diff(moment(day, DISPLAY_DATE_FORMAT).add(2, 'days'), 'days')
  )
}

const DatePicker: FC<DatePickerProps> = props => {
  const { disabled, required, name, value, labels, onChange, dontClearValue, taggedDates } = props
  const [currentValue, setValue] = useState(value)
  const [ref, isOpen, setOpen] = useOutsideClick(false)
  const [selectedDay, setSelectedDay] = useState(value || moment().format(DISPLAY_DATE_FORMAT))
  const [currentMonth, setCurrentMonth] = useState(moment().format(CURRENT_MONTH_FORMAT))
  const [currentMonthIndex, setCurrentMonthIndex] = useState(
    getIndexByCurrentMonth(currentMonth)
  )
  const [yearsDropdownData, setYearsDropdownData] = useState(
    getYearsInitialData(currentMonth, props.noFuture)
  )
  const [calendarData, setCalendarData] = useState(null)
  const [focusDate, setFocusDate] = useState(null)

  const disabledRule = (): DisabledRuleType => {
    if (props.birthday || props.noFuture) {
      return {
        on: true,
        check: isItFuture,
      }
    }

    if (props.noPast) {
      return {
        on: true,
        check: isItPast,
      }
    }

    return {
      on: false,
    }
  }

  useEffect(() => {
    setCalendarData(
      getDataByCurrentMonth(currentMonth, selectedDay, disabledRule())
    )
  }, [selectedDay, currentMonth])

  useEffect(() => {
    setCurrentMonthIndex(getIndexByCurrentMonth(currentMonth))
  }, [currentMonth])

  useEffect(() => {
    if (value !== currentValue) setValue(value)
  }, [value])

  useEffect(() => {
    if (disabled && !dontClearValue) setValue('')
  }, [disabled])

  const handleDayCellClick = (obj: DayDataCellType) => {
    if (!obj?.isCurrentMonth) return

    const selectedDate: string = obj?.['data-date']
    setSelectedDay(selectedDate)
    setValue(selectedDate)
    if (onChange)
      onChange({
        required,
        name: name,
        value: selectedDate,
      })
  }

  const handleKeyDown = (
    event: KeyboardEvent<HTMLElement>,
    obj: DayDataCellType
  ) => {
    //  On press enter key + on focus (Accessibility)
    if (event?.keyCode === 13 && focusDate === obj?.['data-date'])
      handleDayCellClick(obj)
  }

  const handleMonthChange = (obj: ChangeArgs) => {
    const { index } = obj
    const year: string = moment(currentMonth, CURRENT_MONTH_FORMAT).format('YYYY')
    setCurrentMonth(`${index < 9 ? '0' + (index + 1) : index + 1}-${year}`)
  }

  const handleYearChange = (obj: ChangeArgs) => {
    const { index, options } = obj
    const month: string = moment(currentMonth, CURRENT_MONTH_FORMAT).format('MM')
    setCurrentMonth(`${month}-${options?.[index as number]?.label}`)
  }

  const updateYearsData = (
    currentMonth: string,
    yearsDropdownData: YearsDropdownDataType,
    action: string
  ) => {
    const month: string = moment(currentMonth, CURRENT_MONTH_FORMAT).format('MM')
    if (!['01', '12'].includes(month)) return

    const year = Number(moment(currentMonth, CURRENT_MONTH_FORMAT).format('YYYY'))

    const { optionsList } = yearsDropdownData

    if (action === 'lastMonth' && month === '01') {
      const optionsListOutput = [
        ...optionsList.splice(1),
        { label: Number(optionsList[optionsList?.length - 1]?.label) - 1 },
      ]
      setYearsDropdownData({
        selectedIndex: optionsListOutput
          ?.map(obj => obj?.label)
          ?.indexOf(year - 1),
        optionsList: optionsListOutput,
      })
    }

    if (action === 'nextMonth' && month === '12') {
      const optionsListOutput = [
        { label: Number(optionsList[0]?.label) + 1 },
        ...optionsList.slice(0, -1),
      ]
      setYearsDropdownData({
        selectedIndex: optionsListOutput
          ?.map(obj => obj?.label)
          ?.indexOf(year + 1),
        optionsList: optionsListOutput,
      })
    }
  }

  const lastMonthAction = () => {
    setCurrentMonth(updateCurrentMonth(currentMonth, 'lastMonth'))
    updateYearsData(currentMonth, yearsDropdownData, 'lastMonth')
  }

  const nextMonthAction = () => {
    setCurrentMonth(updateCurrentMonth(currentMonth, 'nextMonth'))
    updateYearsData(currentMonth, yearsDropdownData, 'nextMonth')
  }

  return (
    <DatePickerWrapper ref={ref} gridArea={props.gridArea}>
      <InputStyle
        readOnly
        isDatePicker
        fixWidth={props.fixWidth}
        marginBottom={props.marginBottom}
        minHeight={props.minHeight}
        value={currentValue || ''}
        required={required}
        disabled={disabled}
        labelSpacer={props.labelSpacer}
        label={props.label}
        removeLabelEllipsis={props.removeLabelEllipsis}
        flexWrapperCentered={props.flexWrapperCentered}
        placeholder={props.placeholder}
        id={props.id}
        name={name}
        tooltipLabel={props.tooltipLabel}
        clickAction={() => !disabled && setOpen(!isOpen)}
        error={props.error}
      />
      {calendarData !== null && (
        <CalendarWrapper
          flexDirection='column'
          role='dialog'
          aria-modal='true'
          aria-label='Choose Date'
          isOpen={isOpen}
          marginBottom={props.marginBottom}
        >
          <Flex as='header'>
            <DayCell
              as='button'
              type='button'
              data-testid='chevron-left'
              onClick={lastMonthAction}
            >
              <Icon name='chevron-left' size='xxs' />
            </DayCell>
            <Dropdown
              removeBorder
              name='months'
              outsideIndexSelected={currentMonthIndex}
              options={getMonthLabelsList(labels?.months)}
              onChange={handleMonthChange}
            />
            <Dropdown
              removeBorder
              name='years'
              outsideIndexSelected={yearsDropdownData?.selectedIndex}
              options={yearsDropdownData?.optionsList}
              onChange={handleYearChange}
            />
            <DayCell
              as='button'
              type='button'
              data-testid='chevron-right'
              onClick={nextMonthAction}
            >
              <Icon name='chevron-right' size='xxs' />
            </DayCell>
          </Flex>
          <Flex
            flexDirection='column'
            as='table'
            role='grid'
            aria-labelledby='cb-grid-label'
          >
            <thead>
              <Flex as='tr'>
                {getWeekLabelsList(labels?.week)?.map((tuple, index) => (
                  <DayCell as='th' key={`getWeekLabelsList_DayCell_${name}_${index}`} scope='col' abbr={tuple?.[1]}>
                    <Font bold tag='h6'>
                      {tuple?.[0]}
                    </Font>
                  </DayCell>
                ))}
              </Flex>
            </thead>
            <tbody>
              {sliceIntoChunks(calendarData, 7)?.map(
                (chunk: DayDataCellType[], chunkIndex: number) => (
                  <Flex as='tr' key={`tr_${name}_${chunkIndex}`}>
                    {chunk?.map((obj: DayDataCellType, index) => (
                      <DayCell
                        isBody
                        as='td'
                        role='gridcell'
                        key={`DayCell_${name}_${index}`}
                        data-testid={`gridcell-${obj?.value}${
                          obj?.isCurrentMonth ? '-isCurrentMonth' : ''
                        }`}
                        {...obj}
                        onFocus={() =>
                          !obj?.disabled && setFocusDate(obj?.['data-date'])
                        }
                        onBlur={() => setFocusDate(null)}
                        onKeyDown={(event: KeyboardEvent<HTMLElement>) =>
                          !obj?.disabled && handleKeyDown(event, obj)
                        }
                        onClick={() =>
                          !obj?.disabled && handleDayCellClick(obj)
                        }
                      >
                        <Day
                          value={obj.value}
                          isDisabled={obj?.disabled}
                          isSelected={selectedDay === obj?.['data-date']}
                          isTagged={taggedDates?.includes(obj?.['data-date'])}
                          isToday={obj?.isToday}
                        />
                      </DayCell>
                    ))}
                  </Flex>
                )
              )}
            </tbody>
          </Flex>
        </CalendarWrapper>
      )}
    </DatePickerWrapper>
  )
}

DatePicker.defaultProps = {
  noPast: false,
  noFuture: false,
  birthday: false,
  required: false,
  disabled: false,
  fixWidth: false,
  labelSpacer: false,
}

export default DatePicker
